import React, { useState } from "react";
import { Check, X, DoorClosed } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { t } from "@/i18n";

export function BussaButton({ userId, relation, onChange, compact }: { userId: string; relation: any; onChange: (r: any) => void; compact?: boolean }) {
  const [busy, setBusy] = useState(false);
  const state = relation?.knock_state || "none";
  if (!relation || relation.self) return null;

  const knock = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/bussa/${userId}`);
      onChange(data);
      toast.success("Hai bussato. Aspetta che ti apra.");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const respond = async (accept: boolean) => {
    setBusy(true);
    try {
      await api.post(`/bussa/requests/${relation.knock_id}/${accept ? "accept" : "reject"}`);
      onChange({ ...relation, knock_state: accept ? "connected" : "none", can_knock: false });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const base = `btn ${compact ? "px-3 py-2 text-sm" : ""}`;
  if (state === "received") {
    return (
      <div className="flex gap-2" data-testid="bussa-received">
        <button className={`${base} btn-primary`} disabled={busy} onClick={() => respond(true)} data-testid="bussa-accept-btn"><Check size={16} />Apri</button>
        <button className={`${base} btn-ghost`} disabled={busy} onClick={() => respond(false)} data-testid="bussa-reject-btn"><X size={16} />Rifiuta</button>
      </div>
    );
  }
  const map: Record<string, { label: string; cls: string }> = {
    none: { label: t("bussa.none"), cls: "btn-primary" },
    sent: { label: t("bussa.sent"), cls: "bg-[hsl(var(--violet)/.16)] text-[hsl(262_100%_85%)] border-[hsl(var(--violet)/.4)]" },
    connected: { label: t("bussa.connected"), cls: "bg-[hsl(var(--mint)/.14)] text-[hsl(160_80%_75%)] border-[hsl(var(--mint)/.35)]" },
    rejected: { label: t("bussa.rejected"), cls: "bg-white/45 text-ink/56 border-ink/10" },
  };
  const disabled = busy || state !== "none" || !relation.can_knock;
  const cfg = map[state] || map.none;
  const title = state === "rejected" && relation.retry_at ? `Potrai bussare di nuovo dal ${new Date(relation.retry_at).toLocaleDateString("it-IT")}`
    : state === "none" && !relation.can_knock ? (relation.bussa_privacy === "followers" ? "Accetta richieste solo da chi lo segue" : "Non accetta richieste") : undefined;
  return (
    <div className="flex flex-col gap-1">
    <button data-testid="bussa-button" data-state={state} onClick={knock} disabled={disabled && state === "none"} title={title}
      className={`${base} relative overflow-hidden border ${cfg.cls} ${state !== "none" ? "pointer-events-none" : ""} ${state === "none" && !relation.can_knock ? "opacity-40" : ""}`}>
      <DoorClosed size={16} />
      <span>
          {cfg.label}
          {state === "sent" && <span className="ml-0.5">…</span>}
      </span>
    </button>
    {title && <span className="text-[11px] text-ink/62" data-testid="bussa-reason">{title}</span>}
    </div>
  );
}

export function FollowButton({ userId, relation, onChange, compact }: any) {
  const [busy, setBusy] = useState(false);
  if (!relation || relation.self) return null;
  const toggle = async () => {
    setBusy(true);
    try {
      const { data } = relation.following ? await api.delete(`/users/${userId}/follow`) : await api.post(`/users/${userId}/follow`);
      onChange({ ...relation, following: data.following });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <button data-testid="follow-button" disabled={busy} onClick={toggle} className={`btn ${compact ? "px-3 py-2 text-sm" : ""} ${relation.following ? "btn-ghost" : "btn-light"}`}>
      {relation.following ? t("follow.on") : t("follow.off")}
    </button>
  );
}
